import { useAppDispatch, useAppSelector } from "@/app/reduxHooks";
import PickerDetailsHeader from "@/features/adp/components/shared/PickerDetailsHeader";
import { ResolvedIcon } from "@/features/adp/components/shared/ResolvedIcon";
import { useRegisteredDestinations } from "@/features/adp/queries/useDestinationQueries";
import { DataSourceWithExtras } from "@/features/adp/services/common/common.types";
import { closeOverlayAsync } from "@/features/adp/state/overlay.thunks";
import { openOverlay, PickerOverlayData } from "@/features/adp/state/overlaySlice";
import { Button } from "@/ui/button";
import { Card } from "@/ui/card";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/ui/sheet";
import { Skeleton } from "@/ui/skeleton";
import { useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import styles from "./DestinationSheets.module.scss";

const DEFAULT_LIMIT = 6;

const DestinationPicker = () => {
    const { isOpen, type, data } = useAppSelector(state => state.overlay);
    const pickerOverlayData = data as PickerOverlayData;
    const isDestinationPickerOpen = isOpen && type === 'destination-picker';
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();
    const targetData: DataSourceWithExtras[] = queryClient.getQueryData(['sidebar-data'])?.[1];
    const selectedTarget = isDestinationPickerOpen ? targetData?.find(target => target?.data_source_id === Number(pickerOverlayData?.sourceId)) : null;

    const { data: registeredDestinationResponse, isPending } = useRegisteredDestinations({
        allowed_instances: selectedTarget?.data_source_instance,
        enabled: isDestinationPickerOpen,
        page: 1,
        limit: DEFAULT_LIMIT,
    });
    const registeredTargets = registeredDestinationResponse?.targets ?? [];
    const totalCount = registeredDestinationResponse?.total ?? 0;
    const hasMore = totalCount > DEFAULT_LIMIT;

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            dispatch(closeOverlayAsync());
        }
    };

    const handleNewDestinationCick = () => {
        dispatch(openOverlay({
            type: 'destination-configuration',
            data: {
                nodeId: pickerOverlayData?.nodeId,
                ...selectedTarget,
            }
        }));
    }

    const handleViewMoreClick = () => {
        dispatch(openOverlay({
            type: 'destination-registeration-modal',
            data: {
                nodeId: pickerOverlayData?.nodeId,
                sourceId: pickerOverlayData?.sourceId,
                sourceType: pickerOverlayData?.sourceType,
                ...selectedTarget,
            }
        }));
    }

    const handleRegisteredDestinationClick = (target: typeof registeredTargets[number]) => {
        // Mirror the registered-source flow: skip the intermediate Details
        // modal (which only displayed read-only destination/database/schema
        // names) and go straight to the table list. Back from there returns
        // to this picker via the carried `pickerSourceId`/`pickerSourceType`.
        dispatch(openOverlay({
            type: 'registered-destinations-table-list',
            data: {
                nodeId: pickerOverlayData?.nodeId,
                destinationName: target.data_source_name,
                databaseName: target.database_name,
                schemaName: null,
                targetDataSourceId: target.target_data_source_id ?? null,
                pickerSourceId: pickerOverlayData?.sourceId ?? null,
                pickerSourceType: pickerOverlayData?.sourceType ?? null,
            },
        }));
    }

    return (
        <Sheet open={isDestinationPickerOpen} onOpenChange={handleOpenChange}>
            <SheetContent side="right" noBackdrop topOffset={64} className="shadow-none">
                <SheetHeader>
                    <SheetTitle className="text-[18px] font-medium">Destination Details</SheetTitle>
                </SheetHeader>
                <div className={`flex h-full flex-col gap-4 overflow-y-auto px-3 ${styles.thinScroll}`}>
                    <PickerDetailsHeader
                        type="destination"
                        prerequisite={selectedTarget?.prerequisite}
                        imageLink={selectedTarget?.image_link}
                        sourceName={selectedTarget?.data_source_name}
                        label="Destination Name"
                    />

                    <p className="text-sm text-muted-foreground">
                        Select a recent existing destination or add a new one to begin configuring.
                    </p>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {isPending &&
                            ["1", "2", "3", "4", "5", "6"].map((skeletonId) => (
                                <Card key={`source-skeleton-${skeletonId}`} className="border p-3 shadow-none">
                                    <div className="flex items-center gap-3">
                                        <Skeleton className="h-9 w-9 rounded-md" />
                                        <div className="flex-1 space-y-2">
                                            <Skeleton className="h-4 w-24" />
                                            <Skeleton className="h-3 w-32" />
                                        </div>
                                    </div>
                                </Card>
                            ))}

                        {!isPending &&
                            registeredTargets.map((target) => (
                                <Card
                                    key={target?.target_data_source_id}
                                    className="cursor-pointer border p-3 shadow-none transition-colors hover:border-primary/40"
                                    onClick={() => handleRegisteredDestinationClick(target)}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="flex h-9 w-9 items-center justify-center rounded-md border">
                                            <ResolvedIcon
                                                imageLink={target.image_link}
                                                alt={target.data_source_name}
                                                className="h-7 w-7 object-contain"
                                            />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="truncate text-sm font-semibold">
                                                {target.data_source_name}
                                            </p>
                                            <p className="truncate text-xs text-muted-foreground">{target.data_source_name}</p>
                                        </div>
                                    </div>
                                </Card>
                            ))}
                    </div>

                    <div className="space-y-3 pb-4">
                        {hasMore && (
                            <Button
                                type="button"
                                variant="text"
                                className="block w-full text-sm font-semibold"
                                onClick={handleViewMoreClick}
                            >
                                View more
                            </Button>
                        )}
                        <Button variant="outline" className="w-full gap-2" onClick={handleNewDestinationCick}>
                            <Plus className="h-4 w-4" />
                            Add new destination
                        </Button>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
};

export default DestinationPicker;
